import React, { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { AnimatedShinyText } from '@/components/magicui/AnimatedShinyText'
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { AgentThinkingMark } from './AgentThinkingMark'
import { AgentTurnTimeline } from './AgentTurnTimeline'
import { AgentChatToolFallback } from './AgentChatToolFallback'
import type { AgentChatTimelineItem } from './agentChatUtils'
import {
    formatThoughtDurationLabel,
    getLiveTurnHeaderStatus,
    getReasoningBodyAfterHeadline,
    getReasoningHeadline,
} from './agentChatUtils'
import { getToolRunningLabel } from './toolDisplay'
import { StreamingReasoningText } from './StreamingReasoningText'

const activityTextClassName = 'text-sm text-muted-foreground'

type AgentTurnActivityProps = {
    timeline: AgentChatTimelineItem[]
    startedAt?: string | null
    endedAt?: string | null
    live?: boolean
    defaultOpen?: boolean
    className?: string
    onProgress?: () => void
    children?: React.ReactNode
}

type LiveStatus = ReturnType<typeof getLiveTurnHeaderStatus>

function getLiveHeaderLabel(
    liveStatus: LiveStatus,
    hasTimelineItems: boolean,
): string {
    if (liveStatus?.kind === 'tool') {
        return getToolRunningLabel(liveStatus.toolName)
    }

    if (liveStatus?.kind === 'reasoning') {
        const headline = getReasoningHeadline(liveStatus.content)
        if (headline) {
            return headline
        }
    }

    return hasTimelineItems ? 'Working…' : 'Considering…'
}

type LiveTurnBodyProps = {
    timeline: AgentChatTimelineItem[]
    liveStatus: LiveStatus
    onProgress?: () => void
}

function LiveTurnBody({ timeline, liveStatus, onProgress }: LiveTurnBodyProps) {
    const activeReasoningId =
        liveStatus?.kind === 'reasoning' ? liveStatus.segmentId : null

    if (timeline.length === 0) {
        return null
    }

    return (
        <ol className="my-0 flex list-none flex-col gap-2">
            {timeline.map((item) => {
                if (item.type === 'tool') {
                    return (
                        <AgentChatToolFallback
                            key={item.id}
                            toolName={item.toolName}
                        />
                    )
                }

                const isActive = item.id === activeReasoningId
                // Past segments keep their headline; the active segment's
                // headline lives in the shimmer header so only stream the body.
                const content = isActive
                    ? getReasoningBodyAfterHeadline(item.content)
                    : item.content

                if (!content.trim()) {
                    return null
                }

                return (
                    <li key={item.id}>
                        <StreamingReasoningText
                            segmentId={item.id}
                            content={content}
                            enabled={isActive}
                            className={cn(
                                activityTextClassName,
                                'whitespace-pre-wrap leading-relaxed',
                            )}
                            onProgress={onProgress}
                        />
                    </li>
                )
            })}
        </ol>
    )
}

export const AgentTurnActivity: React.FC<AgentTurnActivityProps> = ({
    timeline,
    startedAt = null,
    endedAt = null,
    live = false,
    defaultOpen,
    className,
    onProgress,
    children,
}) => {
    const [open, setOpen] = useState(defaultOpen ?? live)
    const hasTimelineItems = timeline.length > 0
    const isOpen = live ? true : open
    const liveStatus = live ? getLiveTurnHeaderStatus(timeline) : null
    const headerLabel = live
        ? getLiveHeaderLabel(liveStatus, hasTimelineItems)
        : formatThoughtDurationLabel(startedAt, endedAt)

    if (!live && !hasTimelineItems && children === undefined) {
        return null
    }

    return (
        <Collapsible
            open={isOpen}
            onOpenChange={live ? undefined : setOpen}
            className={cn('flex flex-col gap-2', className)}
        >
            {live ? (
                <div
                    className={cn(
                        'flex items-center gap-2.5',
                        activityTextClassName,
                    )}
                >
                    <AgentThinkingMark
                        thinking
                        className="shrink-0 text-foreground"
                    />
                    <AnimatedShinyText className="text-sm" shimmerDuration="3s">
                        {headerLabel}
                    </AnimatedShinyText>
                </div>
            ) : (
                <CollapsibleTrigger
                    className={cn(
                        'inline-flex w-fit max-w-full items-center gap-1 text-left',
                        activityTextClassName,
                        'hover:text-foreground',
                    )}
                >
                    <span>{headerLabel}</span>
                    <ChevronRight
                        className={cn(
                            'size-4 shrink-0 opacity-50 transition-transform',
                            isOpen && 'rotate-90',
                        )}
                    />
                </CollapsibleTrigger>
            )}
            {live ? (
                children !== undefined ? (
                    children
                ) : (
                    <LiveTurnBody
                        timeline={timeline}
                        liveStatus={liveStatus}
                        onProgress={onProgress}
                    />
                )
            ) : (
                <CollapsibleContent className="overflow-hidden">
                    {children !== undefined ? (
                        children
                    ) : hasTimelineItems ? (
                        <AgentTurnTimeline
                            timeline={timeline}
                            onProgress={onProgress}
                        />
                    ) : null}
                </CollapsibleContent>
            )}
        </Collapsible>
    )
}
